import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useTranslation } from 'react-i18next';
import Header from '@/components/layout/Header';
import { getDateLocale } from '@/i18n';

const IconHome = ({ active }: { active: boolean }) => (
  <svg className={`w-5 h-5 ${active ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const IconTasks = ({ active }: { active: boolean }) => (
  <svg className={`w-5 h-5 ${active ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const IconHeadset = ({ active }: { active: boolean }) => (
  <svg className={`w-5 h-5 ${active ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

export default function StaffLayout() {
  const user = useAuthStore((state) => state.user);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const now = new Date();
  const name = user?.displayName || user?.email?.split('@')[0] || 'User';
  const greeting = t('header.greeting', { name });
  const dateLocale = getDateLocale(i18n.language);
  const dateStr = now.toLocaleDateString(dateLocale, options);

  const navItems = [
    { to: '/staff', exact: true, label: t('staffNav.home'), Icon: IconHome },
    { to: '/staff/tasks', exact: false, label: t('staffNav.tasks'), Icon: IconTasks },
    { to: '/staff/support', exact: false, label: t('staffNav.support'), Icon: IconHeadset },
  ];

  const isActive = (to: string, exact: boolean) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  return (
    <div className="flex h-screen flex-col bg-gradient-to-br from-white via-neutral-50 to-neutral-100">
      <Header
        greeting={greeting}
        date={dateStr}
        notificationsPath="/staff/notifications"
      />

      <main className="flex-1 min-h-0 overflow-y-auto relative pb-20">
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor, currentColor 1px)' }} />
        <div className="w-full px-4 py-6 sm:px-6 sm:py-8 lg:px-8 relative z-10">
          <Outlet />
        </div>
      </main>

      {/* Bottom navigation bar — replaces the sidebar for staff */}
      <nav className="sticky bottom-0 z-10 bg-gradient-to-r from-white via-neutral-50 to-neutral-100 border-t border-neutral-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
        <div className="flex items-center justify-around px-4 py-2 max-w-md mx-auto">
          {navItems.map(({ to, exact, label, Icon }) => {
            const active = isActive(to, exact);
            return (
              <Link
                key={to}
                to={to}
                className={`flex flex-col items-center gap-1 px-6 py-2 rounded-xl transition-all duration-200 ${
                  active
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                <Icon active={active} />
                <span className={`text-[11px] font-semibold ${active ? 'text-primary-600' : 'text-neutral-500'}`}>
                  {label}
                </span>
                {active && <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
